import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import TrustSignals from '../../components/ui/TrustSignals';
import UrlInputSection from './components/UrlInputSection';
import ConnectionStatus from './components/ConnectionStatus';
import GasFeeEstimation from './components/GasFeeEstimation';
import NotarizationProgress from './components/NotarizationProgress';
import NotarizationSuccess from './components/NotarizationSuccess';
import ErrorHandler from './components/ErrorHandler';
import ToastNotification from './components/ToastNotification';

const WebpageNotarization = () => {
  const navigate = useNavigate();
  
  // State management
  const [url, setUrl] = useState('');
  const [isValidUrl, setIsValidUrl] = useState(false);
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [networkStatus, setNetworkStatus] = useState('disconnected');
  
  // Modal states
  const [showGasEstimation, setShowGasEstimation] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  
  // Process states
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [notarizationData, setNotarizationData] = useState(null);
  const [error, setError] = useState(null);
  
  // Toast notification
  const [toast, setToast] = useState({ isVisible: false, type: 'info', title: '', message: '' });

  // Check wallet connection on component mount
  useEffect(() => {
    checkWalletConnection();
  }, []);

  const checkWalletConnection = () => {
    const savedConnection = localStorage.getItem('wallet_connected');
    if (savedConnection === 'true') {
      setIsWalletConnected(true);
      setWalletAddress('0x742d35Cc6634C0532925a3b8D4C9db96590b5b8c');
      setNetworkStatus('connected');
      showToast('success', 'Wallet Connected', 'Successfully connected to Polygon network');
    } else {
      setNetworkStatus('disconnected');
    }
  };

  const showToast = (type, title, message) => {
    setToast({ isVisible: true, type, title, message });
  };

  const hideToast = () => {
    setToast({ ...toast, isVisible: false });
  };

  const handleNotarize = () => {
    if (!isWalletConnected) {
      showToast('warning', 'Wallet Required', 'Please connect your wallet to proceed');
      navigate('/wallet-connection');
      return;
    }

    if (!isValidUrl || !url) {
      showToast('error', 'Invalid URL', 'Please enter a valid webpage URL');
      return;
    }

    setShowGasEstimation(true);
  };

  const handleGasConfirm = async () => {
    setShowGasEstimation(false);
    setShowProgress(true);
    setIsLoading(true);
    setCurrentStep(0);

    try {
      // Step 1: Capturing Screenshot
      showToast('info', 'Starting Process', 'Capturing webpage screenshot...');
      await simulateStep(1500);
      setCurrentStep(1);

      // Step 2: Uploading to IPFS
      showToast('info', 'Uploading', 'Storing content on IPFS...');
      await simulateStep(2000);
      setCurrentStep(2);

      // Step 3: Creating Transaction
      showToast('info', 'Processing', 'Creating blockchain transaction...');
      await simulateStep(1500);
      setCurrentStep(3);

      // Step 4: Confirming on Chain
      showToast('info', 'Finalizing', 'Confirming on Polygon blockchain...');
      await simulateStep(2500);
      setCurrentStep(4);

      // Success
      const mockNotarizationData = {
        originalUrl: url,
        screenshotUrl: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop',
        ipfsHash: 'QmYjtig7VJQ6XsnUjqqJvj7QaMcCAwtrgNdahSiFofrE7o',
        ipfsUrl: `https://gateway.pinata.cloud/ipfs/QmYjtig7VJQ6XsnUjqqJvj7QaMcCAwtrgNdahSiFofrE7o`,
        transactionHash: '0x742d35cc6634c0532925a3b8d4c9db96590b5b8c1234567890abcdef',
        timestamp: new Date()?.toISOString(),
        blockNumber: 45892341,
        gasUsed: 142500
      };

      setNotarizationData(mockNotarizationData);
      setShowProgress(false);
      setShowSuccess(true);
      showToast('success', 'Notarization Complete!', 'Your webpage has been successfully archived');

    } catch (error) {
      console.error('Notarization failed:', error);
      setError({
        type: 'blockchain',
        message: 'Failed to complete blockchain transaction. Please try again.',
        code: 'TX_FAILED_001'
      });
      setShowProgress(false);
      setShowError(true);
      showToast('error', 'Process Failed', 'Notarization could not be completed');
    } finally {
      setIsLoading(false);
    }
  };

  const simulateStep = (duration) => {
    return new Promise(resolve => setTimeout(resolve, duration));
  };

  const handleGasCancel = () => {
    setShowGasEstimation(false);
    showToast('info', 'Cancelled', 'Notarization process cancelled');
  };

  const handleProgressClose = () => {
    setShowProgress(false);
    setCurrentStep(0);
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    setNotarizationData(null);
    setUrl('');
    setIsValidUrl(false);
  };

  const handleViewHistory = () => {
    setShowSuccess(false);
    navigate('/archive-details');
  };

  const handleErrorRetry = () => {
    setShowError(false);
    setError(null);
    handleNotarize();
  };

  const handleErrorClose = () => {
    setShowError(false);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationHeader />
      <main className="pt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Connection Status */}
            <ConnectionStatus
              isWalletConnected={isWalletConnected}
              walletAddress={walletAddress}
              networkStatus={networkStatus}
            />

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Main Form */}
              <div className="lg:col-span-2 space-y-6">
                <UrlInputSection
                  url={url}
                  setUrl={setUrl}
                  onNotarize={handleNotarize}
                  isLoading={isLoading}
                  isValidUrl={isValidUrl}
                  setIsValidUrl={setIsValidUrl}
                />
              </div>

              {/* Right Column - Trust Signals */}
              <div className="lg:col-span-1">
                <TrustSignals />
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Modals */}
      <GasFeeEstimation
        isVisible={showGasEstimation}
        url={url}
        onConfirm={handleGasConfirm}
        onCancel={handleGasCancel}
      />
      <NotarizationProgress
        isVisible={showProgress}
        currentStep={currentStep}
        isLoading={isLoading}
        onClose={handleProgressClose}
      />
      <NotarizationSuccess
        isVisible={showSuccess}
        notarizationData={notarizationData}
        onClose={handleSuccessClose}
        onViewHistory={handleViewHistory}
      />
      <ErrorHandler
        isVisible={showError}
        error={error}
        onRetry={handleErrorRetry}
        onClose={handleErrorClose}
      />
      {/* Toast Notification */}
      <ToastNotification
        isVisible={toast?.isVisible}
        type={toast?.type}
        title={toast?.title}
        message={toast?.message}
        onClose={hideToast}
      />
    </div>
  );
};

export default WebpageNotarization;